import PlatformSupportIcons from "../../../components/PlatformSupportIcons/PlatformSupportIcons";
import { useSiteData } from "../../../contexts/SiteDataContext";
import { useLocale } from "../../../i18n/LocaleContext";
import { useLocalized } from "../../../i18n/localized";
import type {
  BooleanFieldItem,
  ComplexBooleanFieldItem,
  Vpn,
  VpnDetailItem,
} from "../../../types";
import DynamicField from "./DynamicField";

interface RatingPageVpnCardDetailsProps {
  vpn: Vpn;
  isOpen: boolean;
}

function getVpnFieldPositiveValue(
  item: ComplexBooleanFieldItem | BooleanFieldItem,
): boolean {
  if (item.type === "complex_boolean_field") {
    return item.isPositive;
  }
  return item.value;
}

function RatingPageVpnCardDetails({
  vpn,
  isOpen,
}: RatingPageVpnCardDetailsProps) {
  const spoilerClass = `overflow-hidden transition-[max-height] duration-200 ease-in-out ${isOpen ? "max-h-[96rem]" : "max-h-0"}`;

  const { siteData } = useSiteData();
  const { t } = useLocale();
  const text = useLocalized();

  const {
    payment_with_crypto,
    is_free,
    zero_tracking,
    jurisdiction_type,
    jurisdiction_country,
    requires_personal_data,
    obsfucation,
    has_support,
    alternative_clients,
    split_tunneling,
    star_verified,
  } = vpn;

  const positiveZeroTracking = Boolean(zero_tracking);
  const positiveDemocratic = jurisdiction_type === "democratic";
  const positivePersonalData = !requires_personal_data;
  const postitiveObsfucation = Boolean(obsfucation);
  const positiveSupport = Boolean(has_support && has_support !== "none");
  const positiveAlternativeClients = Boolean(alternative_clients);
  const positiveSplitTunneling = Boolean(split_tunneling);
  const positiveStarVerified = Boolean(star_verified);
  const positivePayment = Boolean(is_free || payment_with_crypto);
  const jusrisdictionCountryName =
    text(jurisdiction_country, "name") || undefined;
  const comment = text(vpn, "comment");

  const vpnItems: VpnDetailItem[] = [
    // First row on the card, and the only one that can be absent: the tick is
    // a claim we make, not a criterion we score, so an unverified service says
    // nothing rather than saying "Нет". Everything below shifts up a slot when
    // it is gone; the desktop slice below stays at 6 either way, so the
    // collapsed card keeps two full rows of three and just shows one more
    // field (Техподдержка) on unverified services.
    ...(positiveStarVerified
      ? [
          {
            label: t("vpn.field.starVerified"),
            value: true,
            yesText: t("common.yes"),
            type: "boolean_field",
          } as const,
        ]
      : []),
    {
      label: t("vpn.field.jurisdiction"),
      fieldKey: "jurisdiction",
      isPositive: positiveDemocratic,
      hintText: text(siteData?.hint_jusrisdiction, "content") || undefined,
      yesText: jusrisdictionCountryName,
      noText: jusrisdictionCountryName,
      type: "complex_boolean_field",
    },
    {
      label: t("vpn.field.zeroTracking"),
      isPositive: positiveZeroTracking,
      hintText: text(siteData?.hint_zero_tracking, "content") || undefined,
      type: "complex_boolean_field",
    },
    {
      label: t("vpn.field.obfuscation"),
      isPositive: postitiveObsfucation,
      hintText: text(siteData?.hint_obsfucation, "content") || undefined,
      type: "complex_boolean_field",
    },
    {
      label: t("vpn.field.cryptoPayment"),
      isPositive: positivePayment,
      hintText: text(siteData?.hint_has_crypto, "content") || undefined,
      yesText: is_free ? t("common.free") : t("common.yes"),
      noText: t("common.no"),
      type: "complex_boolean_field",
    },
    {
      label: t("vpn.field.personalData"),
      isPositive: positivePersonalData,
      hintText:
        text(siteData?.hint_requires_personal_data, "content") || undefined,
      type: "complex_boolean_field",
      yesText: t("common.no"),
      noText: t("common.yes"),
    },
    {
      label: t("vpn.field.support"),
      value: positiveSupport,
      type: "boolean_field",
    },
    {
      label: t("vpn.field.altClients"),
      value: positiveAlternativeClients,
      noText: t("common.altClientsAbsent"),
      type: "boolean_field",
    },
    {
      label: t("vpn.field.splitTunneling"),
      value: positiveSplitTunneling,
      type: "boolean_field",
    },
    {
      label: t("vpn.field.protocols"),
      value: vpn.protocols.map((protocol) => protocol.name).join(", "),
      type: "labeled_field",
    },
  ];

  const sortedVpnItemsExceptLabeled = vpnItems.filter(
    (item) => item.type !== "labeled_field",
  );
  const sortedVpnItemsForMobile = [
    ...sortedVpnItemsExceptLabeled.filter(
      (item) => !getVpnFieldPositiveValue(item),
    ),
    ...sortedVpnItemsExceptLabeled.filter((item) =>
      getVpnFieldPositiveValue(item),
    ),
    ...vpnItems.filter((item) => item.type === "labeled_field"),
  ];

  const gridSpoilerScheme = {
    mobileAboveSpolier: sortedVpnItemsForMobile.slice(0, 2),
    mobileBelowSpolier: sortedVpnItemsForMobile.slice(2),
    desktopAboveSpolier: vpnItems.slice(0, 6),
    desktopBelowSpolier: vpnItems.slice(6),
  };

  return (
    <div className="text-left text-sm font-normal">
      <div className="grid grid-cols-2 gap-3 md:hidden">
        {gridSpoilerScheme.mobileAboveSpolier.map((item) => {
          return <DynamicField item={item} key={item.label} />;
        })}
      </div>
      <div className="grid-cols-3 gap-3 hidden md:grid">
        {gridSpoilerScheme.desktopAboveSpolier.map((item) => {
          return <DynamicField item={item} key={item.label} />;
        })}
      </div>

      <div className={spoilerClass}>
        <div className="mt-3"></div>

        <div className="grid grid-cols-2 gap-3 md:hidden">
          {gridSpoilerScheme.mobileBelowSpolier.map((item) => {
            return <DynamicField item={item} key={item.label} />;
          })}
        </div>
        <div className="grid-cols-3 gap-3 hidden md:grid">
          {gridSpoilerScheme.desktopBelowSpolier.map((item) => {
            return <DynamicField item={item} key={item.label} />;
          })}
        </div>

        {comment && (
          <>
            <div className="mt-6 pt-3 md:border-t border-almost-white">
              <p className="font-semibold text-sm mb-2">
                {text(siteData?.vpn_card_comment, "title")}
              </p>
              {/* Operators paste raw URLs in here; without break-words a long
                  one overflows the card instead of wrapping. */}
              <p className="text-sm font-normal whitespace-pre-line break-words">
                {comment}
              </p>
            </div>
          </>
        )}

        <PlatformSupportIcons
          vpn={vpn}
          className="mt-3 justify-center items-center flex md:hidden md:border-b-0 border-light-blue-3 pb-3"
        />
      </div>
    </div>
  );
}

export default RatingPageVpnCardDetails;
